import i18n from '@risksmart-app/i18n/src/i18n';

import type { SectionEditorValues } from './config';
import type { FieldSummary } from './constants';

// Keyed on the toggle, not the rule count: the runtime hides any section with
// non-empty `Conditions.tokenGroups`, and the editor reports `enabled: true` with
// zero rules for a token group it cannot represent.
export const hasActiveSectionConditions = (
  config: SectionEditorValues | undefined
): boolean => !!config?.conditionalLogicEnabled;

// `isGroup` too: `elementsToFields` does not recurse, so a `<FieldGroup>`'s own
// `forcedRequired` is always false while its members can be required.
export const isProtectedFromHiding = (
  field: FieldSummary | undefined
): boolean => field?.forcedRequired === true || field?.isGroup === true;

export const getProtectedFieldsInSection = ({
  sectionId,
  containers,
  fields,
}: {
  sectionId: string;
  containers: Record<string, string[]>;
  fields: Record<string, FieldSummary>;
}): string[] =>
  (containers[sectionId] ?? []).filter((fieldId) =>
    isProtectedFromHiding(fields[fieldId])
  );

export interface RequiredFieldSectionViolation {
  sectionId: string;
  fieldIds: string[];
}

export const findRequiredFieldsInConditionalSections = ({
  containers,
  fields,
  sectionConfigs,
}: {
  containers: Record<string, string[]>;
  fields: Record<string, FieldSummary>;
  sectionConfigs: Record<string, SectionEditorValues>;
}): RequiredFieldSectionViolation[] =>
  Object.keys(containers).reduce<RequiredFieldSectionViolation[]>(
    (violations, sectionId) => {
      if (!hasActiveSectionConditions(sectionConfigs[sectionId])) {
        return violations;
      }
      const fieldIds = getProtectedFieldsInSection({
        sectionId,
        containers,
        fields,
      });

      return fieldIds.length > 0
        ? [...violations, { sectionId, fieldIds }]
        : violations;
    },
    []
  );

const formatFieldLabels = (
  fieldIds: string[],
  fields: Record<string, FieldSummary>,
  maxNamed = 2
): string => {
  const labels = fieldIds.map((id) => fields[id]?.name ?? id);
  if (labels.length === 1) {
    return labels[0];
  }
  if (labels.length <= maxNamed) {
    return i18n.t('formEditorConditionalLogic.protectedFieldList.pair', {
      first: labels[0],
      second: labels[1],
    });
  }

  return i18n.t('formEditorConditionalLogic.protectedFieldList.overflow', {
    names: labels.slice(0, maxNamed).join(', '),
    count: labels.length - maxNamed,
  });
};

export const buildRequiredFieldDropReason = (
  fieldIds: string[],
  fields: Record<string, FieldSummary>
): string =>
  i18n.t('formEditorConditionalLogic.protectedFieldsDropRejection', {
    fields: formatFieldLabels(fieldIds, fields),
  });

// In the trigger, not the accordion body: a locked-off toggle never expands.
export const buildRequiredFieldSectionLockReason = (
  fieldIds: string[],
  fields: Record<string, FieldSummary>
): string =>
  i18n.t('formEditorConditionalLogic.protectedFieldsToggleUnavailable', {
    fields: formatFieldLabels(fieldIds, fields),
  });

export const buildRequiredFieldSectionRuleError = (
  fieldIds: string[],
  fields: Record<string, FieldSummary>
): string =>
  i18n.t('formEditorConditionalLogic.protectedFieldsRuleError', {
    fields: formatFieldLabels(fieldIds, fields),
  });
